import { useCallback } from "react";

import {
  AvTabState,
  PubsubVirtualClassStateMessageFromServerType,
} from "../../shared/types/av-state";
import { toastLocalizedOperationCatcher } from "../../utils/alerts";
import { useBreakouts } from "../../utils/breakouts";
import { useClassActions } from "../../utils/class-utils";

import { serverEndSession } from "api/api-server-actions";
import { FOLLOWING_QUERY_PARAM } from "components/constants";
import { ClassSessionCategory, ClassUserType } from "components/server-types";
import { useCapabilities } from "store/capability-hooks";
import { AppDispatch, storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectActiveClassSessionId,
  selectCurrentCourseCode,
  selectCurrentSectionId,
  selectCurrentSectionTitle,
  selectIsClassSessionVirtual,
} from "store/selectors";
import { selectCurrentClassUser } from "store/selectors/class-user";
import { PubsubVirtualClassStateActions } from "store/slices/pubsub-virtual-class-state";
import { GENERAL_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { AutoMuteService } from "utils/auto-mute-service";
import { useAvPubsubMessaging } from "utils/av";
import { useJoinAsSectionAssistant } from "utils/hooks/use-join-as-section-assistant";
import { logInfoWithDebugFlag } from "utils/logger";
import { useQueryParamsManager } from "utils/routing";

const internalStartVirtualClass = async (sectionId: string, isPractice = false) => {
  try {
    await storeApi.class_sessions.create({
      section_id: sectionId,
      category: ClassSessionCategory.VIRTUAL,
      is_practice: isPractice,
    });
  } catch (e) {
    toastLocalizedOperationCatcher("virtual_class_start_failure")(e);
  }
};

const internalStopVirtualClass = async (sessionId: string, currentClassUser: ClassUserType) => {
  try {
    if (currentClassUser.is_in_class_session) {
      // First update current user's participation state to avoid race condition
      await storeApi.class_users.partial_update({
        id: currentClassUser.id,
        is_in_class_session: false,
        is_performing_av_check: false,
      });
    }

    await serverEndSession(sessionId);
  } catch (e) {
    toastLocalizedOperationCatcher("virtual_class_stop_failure")(e);
  }
};

export const useVirtualClassActions = () => {
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const currentClassSessionId = useAppSelector(selectActiveClassSessionId);
  const isClassSessionVirtual = useAppSelector(selectIsClassSessionVirtual);
  const { deleteQueryParams } = useQueryParamsManager();
  const currentClassUser = useAppSelector(selectCurrentClassUser);
  const dispatch = useAppDispatch();
  const currentCourseCode = useAppSelector(selectCurrentCourseCode);
  const currentSectionTitle = useAppSelector(selectCurrentSectionTitle);
  const { joinAsSectionAssistantIfNew } = useJoinAsSectionAssistant();
  const { canAuthorSection } = useCapabilities();

  const { onEndBreakouts } = useBreakouts();
  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();
  const { updateClassUser } = useClassActions();

  const startVirtualClass = useCallback(
    async (isPractice = false) => {
      if (!currentSectionId || !canAuthorSection) return;

      await joinAsSectionAssistantIfNew();

      await internalStartVirtualClass(currentSectionId, isPractice);

      trackEvent("Virtual Class started", {
        ...GENERAL_EVENT_CATEGORY,
        sectionTitle: currentSectionTitle ?? undefined,
        courseCode: currentCourseCode ?? undefined,
      });
    },
    [
      currentSectionId,
      currentSectionTitle,
      currentCourseCode,
      canAuthorSection,
      joinAsSectionAssistantIfNew,
    ],
  );

  const stopVirtualClass = useCallback(async () => {
    if (!currentClassSessionId || !currentClassUser) return;
    await internalStopVirtualClass(currentClassSessionId, currentClassUser);
    onEndBreakouts();
  }, [currentClassSessionId, currentClassUser, onEndBreakouts]);

  // AvCheck can be triggered by the user in two scenarios:
  // 1. When joining a virtual class (isJoin = true)
  // 2. When already in a virtual class and wanting to change settings (isJoin=false)
  const startAvCheck = useCallback(
    (isJoin = false) => {
      updateClassUser({
        is_performing_av_check: true,
      });
      if (isJoin) {
        // This protects against inconsistent state in the pubsub virtual class state
        // If a user can click a join class button, they should always force the join and switch A/V to the tab
        sendPubsubVirtualClassStateMessage({
          action: "join",
          force: true,
        });
      }
    },
    [updateClassUser, sendPubsubVirtualClassStateMessage],
  );

  const showAvCheckInVirtualClass = useCallback(() => {
    startAvCheck();
  }, [startAvCheck]);

  const showAvCheckToJoinVirtualClass = useCallback(
    async (isPractice = false) => {
      // Only instructors can create new sessions
      // Students should only join existing sessions
      if (canAuthorSection && !isClassSessionVirtual) {
        // Instructor creating a new virtual session
        // Backend will set is_performing_av_check=true when session is created
        await startVirtualClass(isPractice);
      } else if (!canAuthorSection && !isClassSessionVirtual) {
        logInfoWithDebugFlag(
          "debug_virtual_class",
          "Student attempted to join virtual class but no session exists, this should not happen",
        );
        // Student trying to join but no session exists - this shouldn't happen
        return;
      }

      // Start AV check to join the existing session
      startAvCheck(true);
    },
    [startVirtualClass, startAvCheck, isClassSessionVirtual, canAuthorSection],
  );

  const startObservingVirtualClass = useCallback(() => {
    dispatch(PubsubVirtualClassStateActions.setObservingSessionId(currentClassSessionId));
  }, [dispatch, currentClassSessionId]);

  const stopObservingVirtualClass = useCallback(() => {
    dispatch(PubsubVirtualClassStateActions.setObservingSessionId(null));
    deleteQueryParams([FOLLOWING_QUERY_PARAM]);
  }, [dispatch, deleteQueryParams]);

  return {
    startObservingVirtualClass,
    stopObservingVirtualClass,
    showAvCheckInVirtualClass,
    showAvCheckToJoinVirtualClass,
    startVirtualClass,
    stopVirtualClass,
  };
};

export const handlePubsubVirtualClassStateMessage = (
  message: PubsubVirtualClassStateMessageFromServerType,
  dispatch: AppDispatch,
) => {
  if (message.action === "mute") {
    logInfoWithDebugFlag(
      "debug_virtual_class",
      "Received class state message with action 'mute', applying auto-mute",
      JSON.stringify(message),
    );
    AutoMuteService.applyAutoMute("force_mute_all");
  } else if (message.action === "sync") {
    logInfoWithDebugFlag(
      "debug_virtual_class",
      "Received class Sync class state, updating all users",
      JSON.stringify(message),
    );
    dispatch(
      PubsubVirtualClassStateActions.updateVirtualClassState({
        users: message.data.users,
        last_updated_at: message.last_updated_at,
      }),
    );
  } else if (message.action === "already-joined") {
    console.warn(
      `Received class state message with action "reject" for user ${message.virtual_user_id},this means the user is already present in the class.`,
    );
    dispatch(PubsubVirtualClassStateActions.updateLastUpdatedAt(message.last_updated_at));
    // The tab that received this message should not be the main AV tab in any case
    dispatch(PubsubVirtualClassStateActions.updateAvTabState(AvTabState.NotMain));
  } else if (message.action === "update") {
    dispatch(
      PubsubVirtualClassStateActions.updateVirtualUserState({
        classUserId: message.virtual_user_id,
        virtualClassUserState: message.data,
        last_updated_at: message.last_updated_at,
      }),
    );
  } else {
    console.warn("Received class state message with unknown action", JSON.stringify(message));
  }
};
